import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@cotera/client/v0/components/ui/dialog';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
// Imported from the module rather than the `components/app` barrel: that barrel
// pulls in the whole ui barrel, which re-exports this grid.
import {
  ModalPageView,
  useOptionalModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { comparisonTakesValue, isComparisonFilterValue } from '../filters';
import type {
  DataGridColumnDataType,
  DataGridComparisonFilterValue,
  DataGridFilterComparison,
} from '../types';

type ComparisonOption = { value: DataGridFilterComparison; label: string };

/**
 * The two that ask about presence rather than value. Last in every list: they
 * are the least-reached-for and the only ones that take no value.
 */
const EMPTINESS: ComparisonOption[] = [
  { value: 'isNull', label: 'is empty' },
  { value: 'isNotNull', label: 'is filled' },
];

/**
 * The conditions a column can be filtered by, named in words rather than
 * symbols.
 *
 * `>` on a date column means "after", and on text it means nothing at all — a
 * row of eight glyphs had the reader translating each one against the column
 * they had in mind. Spelling them out also lets the control be a single
 * dropdown reading as a sentence with the column's name above it, instead of a
 * grid of buttons taking four lines to offer one choice.
 */
const TEXT_COMPARISONS: ComparisonOption[] = [
  { value: 'contains', label: 'contains' },
  { value: 'equals', label: 'is' },
  { value: 'notEquals', label: 'is not' },
  ...EMPTINESS,
];

const NUMBER_COMPARISONS: ComparisonOption[] = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'greaterThan', label: 'is greater than' },
  { value: 'greaterThanOrEqual', label: 'is at least' },
  { value: 'lessThan', label: 'is less than' },
  { value: 'lessThanOrEqual', label: 'is at most' },
  ...EMPTINESS,
];

const DATE_COMPARISONS: ComparisonOption[] = [
  { value: 'equals', label: 'is on' },
  { value: 'notEquals', label: 'is not on' },
  { value: 'greaterThan', label: 'is after' },
  { value: 'greaterThanOrEqual', label: 'is on or after' },
  { value: 'lessThan', label: 'is before' },
  { value: 'lessThanOrEqual', label: 'is on or before' },
  ...EMPTINESS,
];

const comparisonOptionsFor = (
  columnType: DataGridColumnDataType
): ComparisonOption[] => {
  switch (columnType) {
    case 'number':
      return NUMBER_COMPARISONS;
    case 'date':
    case 'timestamp':
      return DATE_COMPARISONS;
    default:
      return TEXT_COMPARISONS;
  }
};

const defaultComparisonFor = (
  columnType: DataGridColumnDataType
): DataGridFilterComparison =>
  columnType === 'text' || columnType === 'category' || columnType === 'unknown'
    ? 'contains'
    : 'equals';

const inputTypeFor = (columnType: DataGridColumnDataType): string => {
  switch (columnType) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'timestamp':
      return 'datetime-local';
    default:
      return 'text';
  }
};

type FormState = {
  comparison: DataGridFilterComparison;
  /** The value input's raw text; parsed against the column type on submit. */
  text: string;
  booleanValue: boolean;
};

/** Reopening a filter starts from the filter that is already applied. */
const initialFormState = (
  value: unknown,
  columnType: DataGridColumnDataType
): FormState => {
  if (isComparisonFilterValue(value)) {
    return {
      comparison: value.comparison,
      text: value.value === null ? '' : String(value.value),
      booleanValue: value.value === true,
    };
  }
  // Bare scalars are the older "substring match" filters.
  const legacy =
    typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return {
    comparison: defaultComparisonFor(columnType),
    text: legacy,
    booleanValue: true,
  };
};

type BooleanChoice = 'true' | 'false' | 'isNull' | 'isNotNull';

const BOOLEAN_CHOICES: { value: BooleanChoice; label: string }[] = [
  { value: 'true', label: 'is true' },
  { value: 'false', label: 'is false' },
  { value: 'isNull', label: 'is empty' },
  { value: 'isNotNull', label: 'is filled' },
];

/**
 * The condition picker: the app's own select, so the trigger, the menu, the
 * checkmark and the chevron are the ones every other dropdown in the app uses.
 *
 * Full width, because the row it sits in is. A control keeping its natural
 * width left "is empty" — a condition that takes no value, so nothing follows
 * it — as a small pill against a wide empty row.
 */
const ConditionSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  /** Shares the row with a value field, rather than filling it alone. */
  besideValue: boolean;
}> = ({ value, onChange, options, besideValue }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      aria-label="Condition"
      className={besideValue ? 'w-40 shrink-0' : 'w-full'}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {options.map((option) => (
        <SelectItem key={option.value} value={option.value}>
          {option.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const DataGridFilterForm: React.FC<{
  columnLabel: string;
  columnType: DataGridColumnDataType;
  value: unknown;
  onSubmit: (value: DataGridComparisonFilterValue) => void;
  onCancel: () => void;
}> = ({ columnLabel, columnType, value, onSubmit, onCancel }) => {
  const [state, setState] = React.useState<FormState>(() =>
    initialFormState(value, columnType)
  );
  const takesValue = comparisonTakesValue(state.comparison);
  const numeric = Number(state.text);
  const canSubmit =
    !takesValue ||
    columnType === 'boolean' ||
    (state.text.trim() !== '' &&
      (columnType !== 'number' || Number.isFinite(numeric)));

  const booleanChoice: BooleanChoice =
    state.comparison === 'isNull'
      ? 'isNull'
      : state.comparison === 'isNotNull'
      ? 'isNotNull'
      : state.booleanValue
      ? 'true'
      : 'false';

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }
    onSubmit({
      kind: 'compare',
      comparison: state.comparison,
      value: !takesValue
        ? null
        : columnType === 'boolean'
        ? state.booleanValue
        : columnType === 'number'
        ? numeric
        : state.text,
    });
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={submit}>
      {/* One line reading as a sentence, under the column's name in the title:
          "date" / "is after" / a date. The condition used to be a grid of eight
          symbol buttons over two rows with a heading of its own, which is four
          lines of dialog spent on a choice a dropdown states in one — and left
          the reader working out what `>` means on the column they had in
          mind. */}
      <div className="flex flex-wrap items-center gap-2">
        {columnType === 'boolean' ? (
          <ConditionSelect
            value={booleanChoice}
            options={BOOLEAN_CHOICES}
            besideValue={false}
            onChange={(choice) =>
              setState((current) => ({
                ...current,
                comparison:
                  choice === 'isNull' || choice === 'isNotNull'
                    ? choice
                    : 'equals',
                booleanValue: choice === 'true',
              }))
            }
          />
        ) : (
          <>
            <ConditionSelect
              value={state.comparison}
              options={comparisonOptionsFor(columnType)}
              besideValue={takesValue}
              onChange={(next) => {
                // Looked up rather than cast: the element hands back a bare
                // string, and the option list is the only thing that says which
                // strings are conditions for this column.
                const picked = comparisonOptionsFor(columnType).find(
                  (option) => option.value === next
                );
                if (picked === undefined) {
                  return;
                }
                setState((current) => ({
                  ...current,
                  comparison: picked.value,
                }));
              }}
            />
            {takesValue ? (
              <Input
                autoFocus
                aria-label="Value"
                className="h-9 min-w-0 flex-1"
                type={inputTypeFor(columnType)}
                placeholder={`Filter ${columnLabel}`}
                value={state.text}
                onChange={(event) =>
                  setState((current) => ({
                    ...current,
                    text: event.target.value,
                  }))
                }
              />
            ) : null}
          </>
        )}
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={!canSubmit}>
          Apply filter
        </Button>
      </div>
    </form>
  );
};

export type DataGridFilterDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnLabel: string;
  columnType: DataGridColumnDataType | undefined;
  /** The filter already applied to the column, if any. */
  value: unknown;
  onApply: (value: DataGridComparisonFilterValue) => void;
};

/**
 * The header's filter editor, shaped by the column's type — a number pad for
 * numbers, true/false for booleans, a date picker for dates.
 *
 * It prefers the app's modal stack so it layers like every other app modal;
 * when the grid renders without one (tests, isolated embeds) it falls back to
 * its own dialog rather than losing the affordance.
 */
export const DataGridFilterDialog: React.FC<DataGridFilterDialogProps> = (
  props
) => {
  const { open, onOpenChange, columnLabel, columnType, value, onApply } = props;
  const modal = useOptionalModalManager();
  // The pushed view outlives this render, so it reads the current props through
  // a ref instead of capturing them — otherwise every parent render would have
  // to pop and re-push to stay current.
  const latest = React.useRef(props);
  React.useEffect(() => {
    latest.current = props;
  });

  React.useEffect(() => {
    if (!open || modal === null) {
      return;
    }
    modal.push({
      view: () => (
        <ModalPageView
          title="Filter"
          subtitle={latest.current.columnLabel}
          className="px-4 py-4"
        >
          <DataGridFilterForm
            columnLabel={latest.current.columnLabel}
            columnType={latest.current.columnType ?? 'unknown'}
            value={latest.current.value}
            onSubmit={(next) => {
              latest.current.onApply(next);
              latest.current.onOpenChange(false);
            }}
            onCancel={() => latest.current.onOpenChange(false)}
          />
        </ModalPageView>
      ),
      onClose: () => latest.current.onOpenChange(false),
    });
    return () => modal.pop();
  }, [open, modal]);

  if (modal !== null) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Filter
          </div>
          <DialogTitle className="truncate">{columnLabel}</DialogTitle>
        </DialogHeader>
        <DataGridFilterForm
          columnLabel={columnLabel}
          columnType={columnType ?? 'unknown'}
          value={value}
          onSubmit={(next) => {
            onApply(next);
            onOpenChange(false);
          }}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
